import { useState } from 'react';
import '../styles/weekday_calendar.css';

const MONTHS     = ['Январь','Февраль','Март','Апрель','Май','Июнь',
                    'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const MONTHS_GEN = ['января','февраля','марта','апреля','мая','июня',
                    'июля','августа','сентября','октября','ноября','декабря'];
const WEEKDAYS_SHORT = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

export const DAY_NAMES = ['Воскресенье','Понедельник','Вторник','Среда','Четверг','Пятница','Суббота'];

export const formatRu = (isoDate) => {
  if (!isoDate || typeof isoDate !== 'string') return String(isoDate || '');
  const [y, m, d] = isoDate.split('-').map(Number);
  return `${d} ${MONTHS_GEN[m - 1]} ${y}`;
};

const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const conflicts = (isoA, isoB, durationDays) => {
  const a = new Date(isoA + 'T00:00:00');
  const b = new Date(isoB + 'T00:00:00');
  return Math.abs((a - b) / 86400000) < durationDays;
};

const TYPE_COLOR = {
  adventures: '#4caf50',
  study:      '#2196f3',
  races:      '#9c27b0',
  vacation:   '#f44336',
};

const TYPE_LABEL = {
  adventures: '⛵',
  study:      '🎓',
  races:      '🏆',
  vacation:   '🏖',
};

// busyDates: [{ isoDate, durationDays, title, type }]
// Returns list of busy events that overlap a given iso day
const getBusyEvents = (iso, busyDates) => {
  if (!busyDates || !busyDates.length) return [];
  const day = new Date(iso + 'T00:00:00');
  return busyDates.filter(ev => {
    const start = new Date(ev.isoDate + 'T00:00:00');
    const end   = new Date(start.getTime() + ((ev.durationDays || 1) - 1) * 86400000);
    return day >= start && day <= end;
  });
};

const WeekdayCalendar = ({
  dayOfWeek = 1,
  durationDays = 1,
  defaultSpaces = 8,
  selected = [],
  onChange,
  anyDay = false,
  busyDates = [],
}) => {
  const [view, setView] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const year  = view.getFullYear();
  const month = view.getMonth();
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const rawFirst   = new Date(year, month, 1).getDay();
  const offset     = (rawFirst + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = Array(offset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toggle = (day) => {
    const d   = new Date(year, month, day);
    const iso = toIso(d);
    const isSel = selected.some(s => s.isoDate === iso);
    if (isSel) {
      onChange(selected.filter(s => s.isoDate !== iso));
    } else {
      onChange([...selected, { isoDate: iso, spaces: defaultSpaces }]);
    }
  };

  const updateSpaces = (isoDate, val) => {
    onChange(selected.map(s =>
      s.isoDate === isoDate ? { ...s, spaces: Math.max(0, Number(val) || 0) } : s
    ));
  };

  const sortedSelected = [...selected].sort((a, b) => (a.isoDate || '').localeCompare(b.isoDate || ''));

  return (
    <div className="wc">
      <div className="wc-nav">
        <button type="button" className="wc-nav-btn"
          onClick={() => setView(new Date(year, month - 1, 1))}>◀</button>
        <strong>{MONTHS[month]} {year}</strong>
        <button type="button" className="wc-nav-btn"
          onClick={() => setView(new Date(year, month + 1, 1))}>▶</button>
      </div>

      <div className="wc-grid">
        {WEEKDAYS_SHORT.map(w => <div key={w} className="wc-hdr">{w}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`_${i}`} className="wc-empty" />;
          const d         = new Date(year, month, day);
          const iso       = toIso(d);
          const isTarget  = anyDay ? true : d.getDay() === dayOfWeek;
          const isPast    = d < today;
          const isSel     = selected.some(s => s.isoDate === iso);
          const isBlocked = isTarget && !isPast && !isSel &&
            selected.some(s => conflicts(iso, s.isoDate, durationDays));
          const busyEvents = getBusyEvents(iso, busyDates);
          const hasBusy    = busyEvents.length > 0;

          const isInRange = durationDays > 1 && selected.some(s => {
            const start = new Date(s.isoDate + 'T00:00:00');
            const end   = new Date(start.getTime() + (durationDays - 1) * 86400000);
            const cur   = new Date(iso + 'T00:00:00');
            return cur > start && cur <= end;
          });

          let cls = 'wc-day';
          if (isSel) cls += ' wc-sel';
          else if (isInRange) cls += ' wc-in-range';
          else if (isTarget && !isPast) cls += isBlocked ? ' wc-blocked' : ' wc-target';
          else cls += ' wc-past';
          if (hasBusy) cls += ' wc-has-busy';

          const clickable = isTarget && !isPast && !isBlocked;

          const titleParts = busyEvents.map(ev =>
            `${TYPE_LABEL[ev.type] || '📌'} ${ev.title}${ev.durationDays > 1 ? ` (${ev.durationDays} дн.)` : ''}`
          );
          const titleStr = isBlocked
            ? ['Пересекается с другим курсом', ...titleParts].join('\n')
            : titleParts.join('\n');

          return (
            <div
              key={day}
              className={cls}
              onClick={() => clickable && toggle(day)}
              title={titleStr || undefined}
            >
              {day}
              {isSel && <span className="wc-check">✓</span>}
              {hasBusy && (
                <span className="wc-busy-dots">
                  {busyEvents.slice(0, 3).map((ev, bi) => (
                    <span
                      key={bi}
                      className="wc-busy-dot"
                      style={{ background: TYPE_COLOR[ev.type] || '#999' }}
                    />
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {busyDates.length > 0 && (
        <div className="wc-legend">
          <span className="wc-legend-item"><span className="wc-busy-dot" style={{ background: TYPE_COLOR.adventures }} />Путешествие</span>
          <span className="wc-legend-item"><span className="wc-busy-dot" style={{ background: TYPE_COLOR.study }} />Обучение</span>
          <span className="wc-legend-item"><span className="wc-busy-dot" style={{ background: TYPE_COLOR.races }} />Регата</span>
          <span className="wc-legend-item"><span className="wc-busy-dot" style={{ background: TYPE_COLOR.vacation }} />Отпуск</span>
        </div>
      )}

      {sortedSelected.length > 0 && (
        <div className="wc-selected">
          <strong>Выбранные даты ({sortedSelected.length}):</strong>
          <ul>
            {sortedSelected.map(s => (
              <li key={s.isoDate}>
                <span className="wc-slot-date">{formatRu(s.isoDate)}</span>
                {durationDays > 1 && (
                  <span className="wc-slot-dur">· {durationDays} дн.</span>
                )}
                <label className="wc-slot-spaces">
                  <input
                    type="number"
                    min="0"
                    value={s.spaces}
                    onChange={e => updateSpaces(s.isoDate, e.target.value)}
                  />
                  мест
                </label>
                <button type="button" className="wc-remove"
                  onClick={() => onChange(selected.filter(x => x.isoDate !== s.isoDate))}>✕</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WeekdayCalendar;
